import { DiscussionComment, htmlToSlate, RichText } from '@amelio/ui-library';
import { useMemo } from 'react';

import { useDiscussionEntryHeader } from '../../hooks/useDiscussionEntryHeader';

import type { User } from '@/types/directory/User';
import type { ObjectiveComment } from '@/types/objectives';

import i18n from '@/plugins/i18n';

interface ObjectiveCommentCardProps {
    comment: ObjectiveComment;
    /** Resolved from `comment.authorId` by the caller; `undefined` both when the id is `null`
     * and when it does not resolve to a known user -- either way the card falls back to the
     * "unknown author" label instead of guessing. */
    author?: User;
}

/** One message in the "Discussion" thread, its body rendered read-only by `RichText`, which never
 * renders raw HTML. */
export default function ObjectiveCommentCard({ comment, author }: ObjectiveCommentCardProps) {
    const header = useDiscussionEntryHeader({ author, date: comment.createdAt });
    const body = useMemo(() => htmlToSlate(comment.body ?? ''), [comment.body]);

    return (
        <DiscussionComment
            {...header}
            dataTestId={`objective-discussion-comment-${comment.id}`}
        >
            <RichText
                value={body}
                readOnly
                toolbar={false}
                surface={'none'}
                locale={i18n.language}
                contentMinHeight={'auto'}
                dataTestId={`objective-discussion-comment-${comment.id}-body`}
            />
        </DiscussionComment>
    );
}
